import { Anchor, Box, Group, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchOk } from '../../lib/api';
import { csrfHeaders, useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

/**
 * ClosingBanner — top-of-shell banner shown only when the signed-in user is
 * in `status='pending_close'`. Wave 5 widened AuthUser['status'] to include
 * the close-grace state and the banner is live; sign-in stays valid through
 * the 30-day grace so a user can land on any AppShell route and tap
 * "Restore my account" inline (no separate Settings hop required).
 */
export function ClosingBanner() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();

  const restore = useMutation({
    mutationFn: async () => {
      const res = await fetchOk('/api/users/me/restore-account', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: '{}',
      });
      return (await res.json()) as { ok: true };
    },
    onSuccess: async () => {
      showToast({ kind: 'success', body: 'Your account is back to active.' });
      await refresh();
      await qc.invalidateQueries();
    },
    onError: (err: unknown) => {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Could not restore your account.',
      });
    },
  });

  if (user === null) return null;
  if (user.status !== 'pending_close') return null;

  const deadline = formatClosingDeadline(user.closingAt);

  return (
    <Box
      style={{
        background: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))',
        borderBottom: '1px solid var(--mantine-color-brand-2)',
        paddingBlock: 10,
        paddingInline: 16,
      }}
    >
      <Group justify="center" gap="md" wrap="wrap">
        <Text size="sm" c="ink.8">
          {deadline === null
            ? 'Your account is closing. Restore any time within 30 days.'
            : `Your account closes on ${deadline}. Restore any time before then.`}
        </Text>
        <Anchor
          component="button"
          type="button"
          size="sm"
          c="brand.7"
          fw={600}
          onClick={() => restore.mutate()}
        >
          Restore my account
        </Anchor>
      </Group>
    </Box>
  );
}

function formatClosingDeadline(iso: string | null | undefined): string | null {
  if (iso === undefined || iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
